/**
 * Tom Select wrapper for BSEE — searchable, taggable <select> enhancement.
 *
 * Opt in per field by marking the select, which is what bseeform's
 * ['enhanced' => true] emits:
 *
 *   <select class="form-select" data-bsee-select>…</select>
 *
 * Everything else is driven by data attributes, so a page can enhance a select
 * without writing any JS:
 *
 *   data-bsee-select-create="true"      allow typing a value that isn't listed
 *   data-bsee-select-max="3"            cap how many can be picked (multiple)
 *   data-bsee-select-placeholder="…"    prompt shown when nothing is chosen
 *   data-bsee-select-plugins="a,b"      override the plugin set
 *
 * WHY TOM SELECT: the obvious pick was bootstrap-select, but its last stable
 * release is v1.13.18 (June 2020), BS5 support exists only in v1.14.0-beta3
 * (April 2022, still beta), and it's a jQuery plugin — BSEE runs jQuery 4,
 * which dropped much of what plugins of that era rely on. Tom Select is the
 * selectize.js successor: no jQuery, a first-party BS5 theme, and actively
 * maintained. `create: true` is the "add an option that isn't in the list"
 * feature this replaced bootstrap-select for.
 *
 * We import the COMPLETE build, so every plugin ships: remove_button,
 * clear_button, checkbox_options, dropdown_header, drag_drop, restore_on_backspace,
 * no_backspace_delete, optgroup_columns, input_autogrow, virtual_scroll.
 *
 * Admin bundle only (biz-bs-ee). Public/ARG pages don't load it. The login page
 * preloads that bundle, so it's cached before the first admin page renders.
 *
 * @see https://tom-select.js.org/
 */

// The package's own entry resolves to dist/esm/tom-select.complete.js — the
// COMPLETE build, so every plugin is available without importing them one by one.
import TomSelect from 'tom-select';

const SELECTOR = '[data-bsee-select]';
const INITIALIZED = 'bseeSelectReady';

/**
 * Plugins for this select. Default is NONE — the same bare control Tom Select's own
 * examples show. An earlier version added `clear_button` to every single select, which
 * put an × on fields that had no business being cleared that way and didn't match
 * upstream. Opt in per field instead:
 *
 *   data-bsee-select-plugins="clear_button"
 *   data-bsee-select-plugins="checkbox_options,remove_button"
 *
 * The one exception is a `multiple` select: without `remove_button` a chosen item can
 * only be dropped with the keyboard, which isn't discoverable. Upstream's own multiple
 * examples carry it too.
 */
const defaultPlugins = (select: HTMLSelectElement): string[] => {
    if (select.multiple) {
        return ['remove_button'];
    }
    return [];
};

const readPlugins = (select: HTMLSelectElement): string[] => {
    const declared = select.dataset.bseeSelectPlugins;
    if (declared === undefined) {
        return defaultPlugins(select);
    }
    // An explicit empty value means "no plugins", including on a multiple select.
    return declared
        .split(',')
        .map(name => name.trim())
        .filter(Boolean);
};

/**
 * Placeholder text: explicit attribute wins, else the select's own empty option
 * (the "-- choose --" row most of our selects already carry), else nothing.
 */
const readPlaceholder = (select: HTMLSelectElement): string | undefined => {
    const declared = select.dataset.bseeSelectPlaceholder;
    if (declared) {
        return declared;
    }
    const blank = select.querySelector<HTMLOptionElement>('option[value=""]');
    if (blank && blank.textContent) {
        return blank.textContent.trim();
    }
    return undefined;
};

const enhance = (select: HTMLSelectElement): void => {
    if (select.dataset[INITIALIZED] === '1') {
        return;
    }
    select.dataset[INITIALIZED] = '1';

    const maxItems = select.dataset.bseeSelectMax;

    new TomSelect(select, {
        create: select.dataset.bseeSelectCreate === 'true',
        plugins: readPlugins(select),
        placeholder: readPlaceholder(select),
        maxItems: select.multiple ? (maxItems ? Number(maxItems) : null) : 1,
        // Match on what the user reads, not the stored value — practice names,
        // not practice ids.
        searchField: ['text'],
        // Keep the chosen row visible in the list rather than hiding it, so a
        // single-select reads like a normal dropdown.
        hideSelected: false,
        // BS5 form-select styling is on the original element; Tom Select copies
        // the classes onto its wrapper so the two stay visually identical.
        copyClassesToDropdown: true,
    });
};

/** Enhance every marked select inside $root (defaults to the document). */
export const initTomSelects = (root: ParentNode = document): void => {
    root.querySelectorAll<HTMLSelectElement>(SELECTOR).forEach(enhance);
};

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => initTomSelects());
    } else {
        initTomSelects();
    }
}

export { TomSelect };
